"use client";

import React, { useEffect } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchWhyChooseUsItems } from "@/store/slices/landingSlice";

const getIcon = (iconName: string) => {
  // @ts-ignore
  return LucideIcons[iconName] || LucideIcons.CheckCircle;
};

const FeatureCard = ({ item, index, mode }: { item: any; index: number; mode: "light" | "dark" }) => {
  const Icon = getIcon(item.icon);
  const isEven = index % 2 === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative"
    >
      <div className={`relative h-full overflow-hidden rounded-3xl p-8 transition-all duration-300 border hover:-translate-y-1 hover:shadow-2xl ${mode === "dark"
        ? "bg-black/40 border-white/5 hover:shadow-[#8B31FF]/10"
        : "bg-white border-gray-100 hover:shadow-[#8B31FF]/10"}`}>

        {/* Animated Background Pattern */}
        <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-[#8B31FF]/5 to-[#8B31FF]/5 blur-2xl transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
        <div className="absolute top-0 right-0 p-6 opacity-5 transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
          <Icon className="h-32 w-32" />
        </div>

        {/* Icon Container */}
        <div className={`relative mb-6 inline-flex h-16 w-16 items-center justify-center rounded-lg shadow-inner transition-colors duration-300 ${mode === "dark" ? "bg-white/5" : "bg-[#f8f9fc]"}`}>
          <motion.div
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            className={`bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] bg-clip-text text-transparent`}
          >
            <Icon className={`h-8 w-8 transition-colors duration-300 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`} />
          </motion.div>
        </div>

        {/* Content */}
        <h3 className={`mb-3 text-xl font-bold transition-colors duration-300 ${mode === "dark" ? "text-white group-hover:text-[#8B31FF]" : "text-gray-900 group-hover:text-[#8B31FF]"}`}>
          {item.title}
        </h3>

        <p className={`leading-relaxed text-sm transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-500"}`}>
          {item.description}
        </p>

        {/* Bottom Decoration */}
        <div className="mt-6 flex items-center gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <div className="h-1 w-full rounded-full bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]" />
        </div>
      </div>
    </motion.div>
  );
};

const WhyChooseUsSection = () => {
  const dispatch = useAppDispatch();
  const { data: items, loading } = useAppSelector((state) => state.landing.whyChooseUsItems);
  const { mode } = useAppSelector((state) => state.theme);

  useEffect(() => {
    dispatch(fetchWhyChooseUsItems());
  }, [dispatch]);

  if (loading) return null;

  return (
    <section className={`relative py-32 overflow-hidden transition-colors duration-700 ${mode === "dark" ? "bg-[#030712]" : "bg-gradient-to-b from-white via-slate-50 to-white"}`}>

      {/* ═══ Border glows ═══ */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF] to-transparent opacity-70" />
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B31FF]/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF]/50 to-transparent" />

      {/* ═══ Background ═══ */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Radial mesh — dark only */}
        {mode === "dark" && (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_55%_at_50%_0%,rgba(79,17,194,0.22),transparent)]" />
        )}

        {/* Grid */}
        <div
          className={`absolute inset-0 ${mode === "dark" ? "opacity-[0.055]" : "opacity-[0.035]"}`}
          style={{
            backgroundImage: mode === "dark"
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Orb 1 — top-right purple */}
        <motion.div
          className={`absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full blur-[120px] ${mode === "dark" ? "bg-[#8B31FF]/40" : "bg-[#8B31FF]/10"}`}
          animate={{ x: [0, -45, 0], y: [0, 35, 0], scale: [1, 1.12, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orb 2 — bottom-left cyan */}
        <motion.div
          className={`absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full blur-[120px] ${mode === "dark" ? "bg-[#8B31FF]/22" : "bg-[#8B31FF]/8"}`}
          animate={{ x: [0, 55, 0], y: [0, -35, 0], scale: [1, 1.18, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orb 3 — centre magenta pulse */}
        <motion.div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full blur-[150px] ${mode === "dark" ? "bg-[#D946EF]/12" : "bg-[#8B31FF]/5"}`}
          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orb 4 — top-left blue */}
        <motion.div
          className={`absolute -top-16 left-1/3 w-[320px] h-[320px] rounded-full blur-[100px] ${mode === "dark" ? "bg-[#2563EB]/22" : "bg-[#2563EB]/6"}`}
          animate={{ x: [0, 35, 0], y: [0, 45, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Diagonal beam — dark only */}
        {mode === "dark" && (
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{ background: "linear-gradient(135deg, transparent 35%, #8B31FF 50%, transparent 65%)" }}
          />
        )}

        {/* Floating particles — dark only */}
        {mode === "dark" &&
          [
            { left: "8%",  top: "15%", delay: 0 },
            { left: "22%", top: "65%", delay: 0.7 },
            { left: "45%", top: "8%",  delay: 1.2 },
            { left: "62%", top: "80%", delay: 0.4 },
            { left: "76%", top: "35%", delay: 1.0 },
            { left: "92%", top: "58%", delay: 1.6 },
          ].map((p, i) => (
            <motion.span
              key={i}
              className="absolute w-[3px] h-[3px] rounded-full bg-[#8B31FF]/60"
              style={{ left: p.left, top: p.top }}
              animate={{ y: [0, -18, 0], opacity: [0.25, 0.75, 0.25] }}
              transition={{ duration: 3.5 + i * 0.4, repeat: Infinity, ease: "easeInOut", delay: p.delay }}
            />
          ))}
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24 items-center">

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium mb-6 transition-all duration-300 ${mode === "dark"
              ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#8B31FF]"
              : "border-purple-100 bg-purple-50 text-purple-700"}`}>
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${mode === "dark" ? "bg-[#8B31FF]" : "bg-purple-400"}`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${mode === "dark" ? "bg-[#8B31FF]" : "bg-purple-500"}`}></span>
              </span>
              Why Choose Us
            </div>

            <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.15] ${mode === "dark" ? "text-white" : "text-gray-900"}`}>
              We Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">Trust</span> <br />
              Through <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">Excellence</span>
            </h2>

            <p className={`text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}>
              Our commitment to quality and innovation sets us apart. We don't just deliver projects; we build lasting partnerships that drive your success in the digital landscape.
            </p>

            <div className="grid grid-cols-2 gap-6">
              <div className={`border-l-4 transition-colors duration-300 pl-6 ${mode === "dark" ? "border-[#8B31FF]" : "border-[#8B31FF]"}`}>
                <div className={`text-3xl font-bold mb-1 transition-colors duration-300 ${mode === "dark" ? "text-white" : "text-gray-900"}`}>98%</div>
                <div className="text-sm text-gray-500 font-medium">Client Retention</div>
              </div>
              <div className={`border-l-4 transition-colors duration-300 pl-6 ${mode === "dark" ? "border-[#8B31FF]" : "border-[#8B31FF]"}`}>
                <div className={`text-3xl font-bold mb-1 transition-colors duration-300 ${mode === "dark" ? "text-white" : "text-gray-900"}`}>100+</div>
                <div className="text-sm text-gray-500 font-medium">Projects Delivered</div>
              </div>
            </div>

                        <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="mt-10 rounded-lg px-8 py-4 text-base font-semibold transition-all shadow-lg hover:shadow-xl bg-[#8B31FF] text-white hover:bg-[#7A2AE0]"
              >
                Start Your Journey
              </motion.button>
                        </motion.div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {items.map((item, index) => (
              <FeatureCard key={item._id || index} item={item} index={index} mode={mode} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
